import apiClient from './api';
import {
  UserProfile,
  Role,
  CreateUserRequest,
  UpdateUserRequest,
  CreateRoleRequest,
  UpdateRoleRequest,
  UserSession,
  PaginatedUsersResponse,
  PaginatedActivityResponse,
  UserManagementFilters,
  RoleManagementFilters,
  AdminResetTwoFactorResponse,
} from '../types/userManagement';

class UserManagementService {
  // User Management APIs
  async getUsers(filters: UserManagementFilters = {}): Promise<PaginatedUsersResponse> {
    const params = new URLSearchParams();
    
    // Backend binds UserListRequest.Search — 'searchTerm' silently no-ops.
    if (filters.searchTerm) params.append('search', filters.searchTerm);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    if (filters.isEmailVerified !== undefined) params.append('isEmailVerified', filters.isEmailVerified.toString());
    if (filters.roleIds?.length) params.append('roleIds', filters.roleIds.join(','));
    if (filters.page !== undefined) params.append('page', filters.page.toString());
    if (filters.pageSize !== undefined) params.append('pageSize', filters.pageSize.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await apiClient.get(`/admin/users?${params.toString()}`);
    return response.data;
  }

  async getUser(userId: number): Promise<UserProfile> {
    const response = await apiClient.get(`/admin/users/${userId}`);
    return response.data;
  }

  async createUser(userData: CreateUserRequest): Promise<UserProfile> {
    const response = await apiClient.post('/admin/users', userData);
    return response.data;
  }

  async updateUser(userId: number, userData: UpdateUserRequest): Promise<UserProfile> {
    const response = await apiClient.put(`/admin/users/${userId}`, userData);
    return response.data;
  }

  async deleteUser(userId: number): Promise<void> {
    await apiClient.delete(`/admin/users/${userId}`);
  }

  async activateUser(userId: number): Promise<void> {
    await apiClient.post(`/admin/users/${userId}/activate`);
  }

  async deactivateUser(userId: number): Promise<void> {
    await apiClient.post(`/admin/users/${userId}/deactivate`);
  }

  // Role Management APIs
  async getRoles(filters: RoleManagementFilters = {}): Promise<Role[]> {
    const params = new URLSearchParams();
    
    if (filters.searchTerm) params.append('search', filters.searchTerm);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    if (filters.page !== undefined) params.append('page', filters.page.toString());
    if (filters.pageSize !== undefined) params.append('pageSize', filters.pageSize.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const queryString = params.toString();
    const url = queryString ? `/Admin/roles?${queryString}` : '/Admin/roles';
    const response = await apiClient.get(url);
    // Handle paginated response structure
    if (response.data && response.data.roles) {
      return response.data.roles;
    }
    // Fallback to direct array if API returns it that way
    return Array.isArray(response.data) ? response.data : [];
  }

  async getRole(roleId: number): Promise<Role> {
    const response = await apiClient.get(`/Admin/roles/${roleId}`);
    return response.data;
  }

  async createRole(roleData: CreateRoleRequest): Promise<Role> {
    const response = await apiClient.post('/Admin/roles', roleData);
    return response.data;
  }

  async updateRole(roleId: number, roleData: UpdateRoleRequest): Promise<Role> {
    const response = await apiClient.put(`/Admin/roles/${roleId}`, roleData);
    return response.data;
  }

  async deleteRole(roleId: number): Promise<void> {
    await apiClient.delete(`/Admin/roles/${roleId}`);
  }

  // User-Role Assignment APIs
  async assignRoleToUser(userId: number, roleId: number): Promise<void> {
    await apiClient.post(`/Admin/users/${userId}/roles`, { roleId });
  }

  async removeRoleFromUser(userId: number, roleId: number): Promise<void> {
    await apiClient.delete(`/Admin/users/${userId}/roles/${roleId}`);
  }

  // Session Management APIs
  async getUserSessions(userId: number): Promise<UserSession[]> {
    const response = await apiClient.get(`/admin/users/${userId}/sessions`);
    return response.data;
  }

  async revokeUserSessions(userId: number): Promise<void> {
    await apiClient.post(`/admin/users/${userId}/revoke-sessions`);
  }

  // User Activity APIs
  async getUserActivity(
    userId: number, 
    page: number = 1, 
    pageSize: number = 50
  ): Promise<PaginatedActivityResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    
    const response = await apiClient.get(
      `/admin/users/${userId}/activity?${params.toString()}`
    );
    return response.data;
  }

  // Admin 2FA Management
  async adminResetUser2FA(userId: number, reason: string): Promise<AdminResetTwoFactorResponse> {
    const response = await apiClient.post(`/admin/users/${userId}/reset-2fa`, { reason });
    return response.data;
  }

  // Admin Password Reset
  async adminResetPassword(userId: number, request: { newPassword: string }): Promise<void> {
    await apiClient.post(`/admin/users/${userId}/reset-password`, request);
  }
}

export const userManagementService = new UserManagementService();